import type { AuthMode } from '@src/auth/AuthContext';

/**
 * Detect whether the terminal is running inside a native webview or a
 * standalone browser tab.
 *
 * Detection priority:
 *  1. Native bridge objects — strongest signal
 *  2. User-agent heuristics — weaker fallback
 *
 * Returns 'unknown' when we cannot positively confirm the environment
 * (including SSR / no window). This allows the auth layer to wait for the
 * definitive `7punches:init` postMessage before committing to an auth flow.
 */
export function detectMode(): AuthMode {
    if (typeof window === 'undefined') return 'unknown';

    if (hasNativeBridge()) return 'webview';
    if (hasWebviewUserAgent()) return 'webview';

    return 'unknown';
}

function hasNativeBridge(): boolean {
    return !!(
        window.ReactNativeWebView ||
        window.webkit?.messageHandlers?.sevenPunches ||
        window.sevenPunches
    );
}

function hasWebviewUserAgent(): boolean {
    const ua = navigator.userAgent;
    // Android WebView marker
    if (/; wv\b/.test(ua)) return true;
    // iOS WKWebView inside standalone container (no Safari in UA)
    if (/iPhone|iPad/.test(ua) && !/Safari/.test(ua)) return true;
    return false;
}
